import { useState, useEffect, useCallback, SyntheticEvent } from "react";
import { LatLngTuple } from "leaflet";
import { useHistory, useLocation } from "react-router-dom";
//--------------------------------------------------------------------------------------------
import { myAxios, getBrandLogo } from "src/components/common/functions";
import { setAccessToken } from "src/components/common/authToken";
import Notification from "src/components/common/notification";
import { useMyStore } from "src/components/main/mainStore";
import { json } from "src/components/common/types";
import Form from "./form";
//--------------------------------------------------------------------------------------------
export const user:{
    coords: LatLngTuple,
    accuracy: number,
    token?: string,
    [key: string]: any
} = {
    coords: [37.6148754, -122.0874473],
    accuracy: 0,
    token: "", 
};
export function Login(){
    const setShowLoader = useMyStore(state => state.setShowLoader);
    const setAuthenticated = useMyStore(state => state.setAuthenticated);
    const setUserInfo = useMyStore(state => state.setUserInfo);
    //--------------------------------------------------------------------------------------------
    const history = useHistory();
    const location = useLocation<{from: json}>();
    //--------------------------------------------------------------------------------------------
    const { from } = location.state || { from: { pathname: "/" } };
    //--------------------------------------------------------------------------------------------
    const defaultNotification: {
        show: boolean, 
        title?: string, 
        msg?: string, 
        callBack?: Function, 
        preventClose?: boolean
    } = { 
        show: false, 
        title: "", 
        msg: ""
    };
    const [notification, setNotification] = useState(defaultNotification);
    const [email, setEmail] = useState(localStorage.getItem("btlEmail") || "");
    // Password is never persisted anymore — session continuity comes from the
    // HttpOnly refresh cookie + silent boot refresh, not a stored password.
    const [password, setPassword] = useState("");
    const [keepLogIn, setKeepLogIn] = useState(true);
    const [showNewPasswordInput, setShowNewPasswordInput] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [showForgetPassword, setShowForgetPassword] = useState(false);
    const defaultPop: {
        show:boolean, 
        pop?: string, 
        popName?: string, 
        callBack?: Function
    } = {
        show: false, 
        pop: "", 
        popName: ""
    }
    const [pop, setPop] = useState(defaultPop);
    //--------------------------------------------------------------------------------------------
    const handleLoginEmail = (e: SyntheticEvent) => setEmail((e.currentTarget as HTMLInputElement).value.toLowerCase());
    const handleLoginPassword = (e: SyntheticEvent) => setPassword((e.currentTarget as HTMLInputElement).value);    
    const handleLoginNewPassword = (e: SyntheticEvent) => setNewPassword((e.currentTarget as HTMLInputElement).value);
    const closeIt = () => {
        setNotification({ show : false });
        setPop({show: false});
    }
    const handleLogin = useCallback(async(currentPassword: string | null) => {
        const formData = new FormData();
        formData.append("email", email);
        formData.append("password", currentPassword === null? password : currentPassword);
        formData.append("version", user.version);
        formData.append("keepLogIn", String(keepLogIn)); // persistent vs session refresh cookie
        //---------------------------------------------------------------
        setShowLoader(true);
        const res: any = await myAxios("auth/login.php", formData, undefined, undefined, true);
        setShowLoader(false);
        if(!res) return;
        //---------------------------------------------------------------
        const cont2 = () => {
            history.replace({...from, state: {...from.state, external: true}});
        }
        const cont1 = () => {
            //-------------------------------------------------------------------------------
            user.email = email; // update user
            //-------------------------------------------------------------------------------
            if(navigator.geolocation){
                navigator.geolocation.getCurrentPosition(pos => {
                    //-------------------------------------------------------------------------------
                    user.coords = [pos.coords.latitude, pos.coords.longitude]; // update user
                    user.accuracy = pos.coords.accuracy; // update user
                    //-------------------------------------------------------------------------------
                }, error => console.warn(error.message), {enableHighAccuracy: true, timeout: 15000});
            }else{
                window.alert("Unable to get your location!");
            }
            if(res.data.popName){
                setPop({
                    popName: res.data.popName, 
                    pop: res.data.pop, 
                    show: true,
                    callBack: cont2
                });
            }else{
                cont2();
            }
        }
        if (res.data.accessToken) {
            setAccessToken(res.data.accessToken); // in-memory only
        }
        if(res.data.code){
            setAuthenticated(true);
            localStorage.setItem("btlEmail", email);
            // Password is no longer stored. The HttpOnly refresh cookie (set by
            // login.php) provides "keep me logged in" via the silent boot refresh.
            localStorage.removeItem("btlPassword"); // clean up any legacy stored password
            if(keepLogIn){
                localStorage.setItem("btlKeepLogIn", "true");
            }
            //-------------------------------------------------------------------------------
            for (let key of Object.keys(res.data.user)) { 
                if (key !== "coords") user[key] = res.data.user[key]; // update user
            }
            //-------------------------------------------------------------------------------
            setUserInfo(user); // update user state
            if (res.data.update && user.versionMessage) { 
                setNotification(
                    { 
                        show : true, 
                        title: "Update", 
                        msg: user.versionMessage, 
                        callBack: cont1
                    }
                );
            }else{
                cont1();
            }
        } else { 
            setNotification({show : true, title: res.data.title, msg: res.data.msg});
            localStorage.setItem("btlKeepLogIn", "false");
            setKeepLogIn(false);
        }
    }, [keepLogIn, email, from, history, password, setAuthenticated, setShowLoader, setUserInfo])
    const handleKeepLogIn = () => { 
        setKeepLogIn(!keepLogIn);
    }
    const ForgetPasswordPopup = () => { 
        setShowForgetPassword(true);
        setPassword("");
    }
    const ChangePasswordPopup = () => { 
        setShowNewPasswordInput(true);
    }
    const handleForgetPassword = useCallback(async () => { 
        const formData = new FormData();
        formData.append("email", email);
        //---------------------------------------------------------------
        setShowLoader(true);
        const res: any = await myAxios("auth/forgetPassword.php", formData, undefined, undefined, false);
        setShowLoader(false);
        if(!res) return;
        //---------------------------------------------------------------
        setNotification({show : true, title: res.data.title, msg: res.data.msg});
        setShowForgetPassword(false);
    },[email, setShowLoader])
    const handleChangePassword = useCallback(async () => { 

        if (newPassword === "") { 
            setNotification({show : true, title: "Error", msg: "Input New Password!"});
            return;
        }
        if(newPassword === password){
            setNotification({show : true, title: "Error", msg: "Same Password!"});
            return;
        }
        const formData = new FormData();
        formData.append("email", email);
        formData.append("password", password);
        formData.append("newPassword", newPassword);
        //---------------------------------------------------------------
        setShowLoader(true);
        const res: any = await myAxios("auth/changePassword.php", formData, undefined, undefined, false);
        setShowLoader(false);
        if (!res) return;
        //---------------------------------------------------------------
        if (res.data.code) {
            setNotification({show : true, title: res.data.title, msg: res.data.msg, preventClose: true});
            setTimeout(() => {
                setPassword(newPassword);
                localStorage.removeItem("btlPassword"); // never persist the password
                setShowNewPasswordInput(false);
                handleLogin(newPassword);
            }, 2000);
        }
        else{
            setNotification({show : true, title: res.data.title, msg: res.data.msg});
        }
    },[email, handleLogin, newPassword, password, setShowLoader])
    //--------------------------------------------------------------------------------------------
    useEffect(() => {
        let isMounted = true;
        const Main = async() => {
            // Legacy cleanup: password persistence has been removed, so the old
            // Remember Password flag no longer controls anything.
            localStorage.removeItem("btlRememberPassword");
            if(!user.version){
                const formData = new FormData();
                //---------------------------------------------------------------
                setShowLoader(true);
                const res: any = await myAxios("auth/getVersion.php", formData, undefined, undefined, false);
                if(!isMounted) return;
                setShowLoader(false);
                if(!res) return;
                //---------------------------------------------------------------
                user.version = res.data.version; // update user
                user.versionMessage = res.data.message; // update user
                //---------------------------------------------------------------
            }
            window.onkeydown = e => {
                if(e.key === "Enter"){
                    e.preventDefault();
                    handleLogin(password);   
                }
            }
        }
        Main();
        return () => {
            window.onkeydown = () => {}
            isMounted = false;
        };
    },[
        handleLogin, 
        email, 
        from, 
        history, 
        keepLogIn, 
        notification, 
        password, 
        showForgetPassword, 
        showNewPasswordInput, 
        setShowLoader
    ])
    //--------------------------------------------------------------------------------------------
    return (
        <div className = "loginBox">
            <img className = "cssLoginLogo" src = {getBrandLogo()} alt = ""/>
            <input className = "bwinput" type = "email" placeholder = "Email" id = "email" value = {email} onChange = {handleLoginEmail} required autoComplete = "off"/>
            {!showForgetPassword?<input className = "bwinput" type = "password" placeholder = "Password" id = "password" value = {password} onChange = {handleLoginPassword} required autoComplete = "off"/>:null}
            {showNewPasswordInput?<>
                <input className = "bwinput" type = "password" placeholder = "New Password" id = "newPassword" value = {newPassword} onChange = {handleLoginNewPassword} required autoComplete = "off"/>
                <div>
                    <button type = "button" className = "bwbtn" onClick = {handleChangePassword}>CHANGE</button>
                    <button type = "button" className = "bwbtn" onClick = {() => setShowNewPasswordInput(false)}>GO BACK</button>
                </div>
            </>:null}
            {showForgetPassword?<>
                <div>
                    <button type = "button" className = "bwbtn" onClick = {handleForgetPassword}>SEND PASSWORD</button>
                    <button type = "button" className = "bwbtn" onClick = {() => setShowForgetPassword(false)}>GO BACK</button>
                </div>
            </>:null}
            {!showNewPasswordInput&&!showForgetPassword?<>
            <div>
                <button type = "button" className = "bwbtn" onClick = {() => handleLogin(null)}>SIGN IN </button>
                <button type = "button" className = "bwbtn" onClick = {ForgetPasswordPopup}>FORGET PASSWORD</button>
            </div>
            <table>
                <tbody>
                    <tr>
                        <td>
                            <button type = "button" className = "bwbtn" onClick = {ChangePasswordPopup}>CHANGE PASSWORD</button>
                        </td>
                        <td>
                            <input id = "keepMeLogIn" type = "checkbox" checked = {keepLogIn} onChange = {handleKeepLogIn}/>
                        </td>
                        <td>
                            <label id = "keepMeLogInLabel" htmlFor = "keepMeLogIn">Keep Logged In</label>
                        </td>
                    </tr>
                </tbody>    
            </table>
            </>:null}
            {pop.show?
            <Form
                pop = {pop.pop || ""}
                popName = {pop.popName || ""}
                closeIt = {
                    (pop.callBack?
                        pop.callBack:
                        closeIt
                    )
                }
            />
            :null}
            {notification.show?
            <Notification
                title = {notification.title || ""}
                msg = {notification.msg || ""}
                closeIt = {notification.preventClose ?
                () => {} :
                (notification.callBack ?
                    notification.callBack :
                    closeIt
                )}
            />:null}
        </div>
    );
}